<script setup lang="ts">
import { computed } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import { useDateTimePicker } from '../composables/DateTimePickerState'
import { usePrevioDS } from '../../../composables/helpers'
import HeaderCell from './HeaderCell.vue'
import DateCell from './DateCell.vue'

type Emits = {
  (e: 'select'): void
}

const emits = defineEmits<Emits>()

const model = defineModel<Dayjs | null>()

const props = defineProps({
  monthIndex: {
    type: Number,
    required: true,
  },
  isFirstSlide: {
    type: Boolean,
    default: false,
  },
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
})

const navigation = useDateTimePicker()
const ds = usePrevioDS()

const translatedDays = computed<string[]>(() => {
  return Array(7)
    .fill(0)
    .map((_, n) =>
      dayjs()
        .tz()
        .day(n + 1)
        .format('ddd')
        .toUpperCase(),
    )
})

const getFirstDayOffset = (year: number, month: number): number => {
  const firstDay = dayjs().tz().year(year).month(month).date(1).day()

  return (firstDay + 6) % 7
}

const getDatesInMonth = (year: number, month: number): Dayjs[] => {
  const daysInMonth = dayjs().tz().year(year).month(month).daysInMonth()

  return Array.from({ length: daysInMonth }, (_, i) =>
    dayjs()
      .tz()
      .year(year)
      .month(month)
      .date(i + 1),
  )
}

const getPreviousMonthDates = (year: number, month: number): Dayjs[] => {
  const offset = getFirstDayOffset(year, month)
  const prevMonth = dayjs()
    .tz()
    .year(year)
    .month(month - 1)
  const daysInPrevMonth = prevMonth.daysInMonth()

  return Array.from({ length: offset }, (_, i) => prevMonth.date(daysInPrevMonth - offset + i + 1))
}

const getNextMonthDates = (year: number, month: number): Dayjs[] => {
  const totalDays = getFirstDayOffset(year, month) + getDatesInMonth(year, month).length
  const remainingDays = (7 - (totalDays % 7)) % 7

  const nextMonth = dayjs()
    .tz()
    .year(year)
    .month(month + 1)

  return Array.from({ length: remainingDays }, (_, i) => nextMonth.date(i + 1))
}

const calendar = computed<Dayjs>(() => {
  if (props.isFirstSlide) {
    return dayjs().tz().year(navigation.currentCalendar().year()).month(props.monthIndex)
  }

  return dayjs().tz().year(navigation.secondCalendar().year()).month(props.monthIndex)
})
</script>

<template>
  <div class="ds-date-time-picker__calendar">
    <div class="ds-date-time-picker__calendar-header">
      <header-cell v-for="day in translatedDays" :key="day" :text="day" />
    </div>

    <div class="ds-date-time-picker__calendar-grid">
      <date-cell
        v-for="date in getPreviousMonthDates(calendar.year(), calendar.month())"
        :key="date.format()"
        v-model="model"
        :date="props.isFirstSlide ? date : null"
        :default-time="props.defaultTime"
        dim
        @select="emits('select')"
      />
      <date-cell
        v-for="date in getDatesInMonth(calendar.year(), calendar.month())"
        :key="date.format()"
        v-model="model"
        :date="date"
        :default-time="props.defaultTime"
        @select="emits('select')"
      />
      <date-cell
        v-for="date in getNextMonthDates(calendar.year(), calendar.month())"
        :key="date.format()"
        v-model="model"
        :date="ds.screen().isMobile ? date : props.isFirstSlide ? null : date"
        :default-time="props.defaultTime"
        dim
        @select="emits('select')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ds-date-time-picker {
  &__calendar {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  &__calendar-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    width: 100%;
  }

  &__calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    width: 100%;
  }
}
</style>
